import Device from '../../common/device';
import States from '../../common/states';
import { ApiError } from '../../types/errors';
import Constants from '../../utils/constants';
import {
  ContinueMovieItem,
  DetailedMovieItem,
  FiltersType,
  MainScreenCategory,
  MovieCategoryType,
  MovieItem,
  SelectionType,
} from '../models/cinema';
import sendRequest from '../response';

export async function main(): Promise<MainScreenCategory[]> {
  const res = await sendRequest(`${Constants.apiBaseUrlV3}/portal/main`, {
    ...Device.getDeviceHeaders(),
    Accept: 'application/json',
    'HTTP-X-TOKEN': States.mainState.mediaToken,
    'HTTP-X-MODE': States.settingsState.devServer ? 'dev' : '',
  });
  if (!res.success) throw new ApiError(res.error.code, res.error.message);

  return (res.data as any[]).filter(e =>
    Constants.supportedMainCarousels.includes(e.type),
  );
}

export async function filters(): Promise<FiltersType> {
  const res = await sendRequest(`${Constants.apiBaseUrlV3}/portal/listFilter`, {
    ...Device.getDeviceHeaders(),
    'HTTP-X-TOKEN': States.mainState.mediaToken,
    'HTTP-X-MODE': States.settingsState.devServer ? 'dev' : '',
  });
  if (!res.success) throw new ApiError(res.error.code, res.error.message);

  return res.data;
}

export async function movies({
  category = 0,
  page = 1,
  limit = 12,
}): Promise<MovieCategoryType> {
  const res = await sendRequest(
    `${Constants.apiBaseUrlV3}/portal/category` +
      `?cid=${category}&page=${page}&limit=${limit}`,
    {
      ...Device.getDeviceHeaders(),
      Accept: 'application/json',
      'HTTP-X-TOKEN': States.mainState.mediaToken,
      'HTTP-X-MODE': States.settingsState.devServer ? 'dev' : '',
    },
  );
  if (!res.success) throw new ApiError(res.error.code, res.error.message);

  return res.data;
}

export async function details(id: number): Promise<DetailedMovieItem> {
  const res = await sendRequest(
    `${Constants.apiBaseUrlV3}/portal/movie?movie_id=${id}`,
    {
      ...Device.getDeviceHeaders(),
      Accept: 'application/json',
      'HTTP-X-TOKEN': States.mainState.mediaToken,
      'HTTP-X-MODE': States.settingsState.devServer ? 'dev' : '',
    },
  );
  if (!res.success) throw new ApiError(res.error.code, res.error.message);

  return res.data;
}

export async function recommedation(id: number): Promise<MovieItem[]> {
  const res = await sendRequest(
    `${Constants.apiBaseUrlV3}/portal/recommendation?movie_id=${id}`,
    {
      ...Device.getDeviceHeaders(),
      Accept: 'application/json',
      'HTTP-X-TOKEN': States.mainState.mediaToken,
      'HTTP-X-MODE': States.settingsState.devServer ? 'dev' : '',
    },
  );
  if (!res.success) throw new ApiError(res.error.code, res.error.message);

  return res.data;
}

export async function url(id: number): Promise<[string, string]> {
  const res = await sendRequest(
    `${Constants.apiBaseUrlV3}/portal/file/url?link_id=${id}`,
    {
      ...Device.getDeviceHeaders(),
      Accept: 'application/json',
      'HTTP-X-TOKEN': States.mainState.mediaToken,
      'HTTP-X-MODE': States.settingsState.devServer ? 'dev' : '',
    },
  );
  if (!res.success) throw new ApiError(res.error.code, res.error.message);
  return [res.data.url, res.data.view_id];
}

export async function favorites({
  page = 1,
  limit = 12,
}): Promise<MovieItem[]> {
  const res = await sendRequest(
    `${Constants.apiBaseUrlV3}/portal/bookmark?page=${page}&limit=${limit}`,
    {
      ...Device.getDeviceHeaders(),
      Accept: 'application/json',
      'HTTP-X-TOKEN': States.mainState.mediaToken,
      'HTTP-X-MODE': States.settingsState.devServer ? 'dev' : '',
    },
  );
  if (!res.success) throw new ApiError(res.error.code, res.error.message);

  return res.data;
}

export async function query({
  page,
  limit,
  category,
  distributor,
  genres,
  countries,
  year,
  sort,
}: {
  page?: number;
  limit?: number;
  category?: number;
  distributor?: number;
  genres?: number[];
  countries?: number[];
  year?: number;
  sort?: number;
}): Promise<MovieItem[]> {
  page ??= 1;
  limit ??= 12;

  let q = `category=${category ?? 0}`;
  if (distributor !== undefined) q += `&distributor=${distributor}`;
  if (Array.isArray(genres)) q += `&genre=${genres.join('|')}`;
  if (Array.isArray(countries)) q += `&country=${countries.join('|')}`;
  if (year !== undefined) q += `&year=${year}`;
  if (sort !== undefined) q += `&id_sort=${sort}`;

  const res = await sendRequest(
    `${Constants.apiBaseUrlV3}/portal/search?page=${page}&limit=${limit}&${q}`,
    {
      ...Device.getDeviceHeaders(),
      Accept: 'application/json',
      'HTTP-X-TOKEN': States.mainState.mediaToken,
      'HTTP-X-MODE': States.settingsState.devServer ? 'dev' : '',
    },
  );
  if (!res.success) throw new ApiError(res.error.code, res.error.message);

  return res.data;
}

export async function history({
  page = 1,
  limit = 12,
}): Promise<ContinueMovieItem[]> {
  const res = await sendRequest(
    `${Constants.apiBaseUrlV3}/portal/history?page=${page}&limit=${limit}`,
    {
      ...Device.getDeviceHeaders(),
      Accept: 'application/json',
      'HTTP-X-TOKEN': States.mainState.mediaToken,
      'HTTP-X-MODE': States.settingsState.devServer ? 'dev' : '',
    },
  );
  if (!res.success) throw new ApiError(res.error.code, res.error.message);

  return res.data;
}

export async function continueWatch(): Promise<ContinueMovieItem[]> {
  const res = await sendRequest(`${Constants.apiBaseUrlV3}/portal/continue`, {
    ...Device.getDeviceHeaders(),
    Accept: 'application/json',
    'HTTP-X-TOKEN': States.mainState.mediaToken,
    'HTTP-X-MODE': States.settingsState.devServer ? 'dev' : '',
  });
  if (!res.success) throw new ApiError(res.error.code, res.error.message);

  return res.data;
}

export async function removeFromHistory(id: number) {
  const res = await sendRequest(
    `${Constants.apiBaseUrlV3}/portal/movie/clear?movie_id=${id}`,
    {
      ...Device.getDeviceHeaders(),
      Accept: 'application/json',
      'HTTP-X-TOKEN': States.mainState.mediaToken,
      'HTTP-X-MODE': States.settingsState.devServer ? 'dev' : '',
    },
  );
  if (!res.success) throw new ApiError(res.error.code, res.error.message);
}

export async function toggleFavorite(id: number): Promise<boolean> {
  const res = await sendRequest(
    `${Constants.apiBaseUrlV3}/portal/bookmark/change?file_id=${id}`,
    {
      ...Device.getDeviceHeaders(),
      Accept: 'application/json',
      'HTTP-X-TOKEN': States.mainState.mediaToken,
      'HTTP-X-MODE': States.settingsState.devServer ? 'dev' : '',
    },
  );
  if (!res.success) throw new ApiError(res.error.code, res.error.message);

  return res.data.active;
}

export async function collections(): Promise<SelectionType[]> {
  const res = await sendRequest(
    `${Constants.apiBaseUrlV3}/portal/listMovieSelect`,
    {
      ...Device.getDeviceHeaders(),
      Accept: 'application/json',
      'HTTP-X-TOKEN': States.mainState.mediaToken,
      'HTTP-X-MODE': States.settingsState.devServer ? 'dev' : '',
    },
  );
  if (!res.success) throw new ApiError(res.error.code, res.error.message);

  return res.data;
}

export async function heartbeat(
  linkId: number,
  viewId: string,
  seconds: number,
  start?: boolean,
) {
  let url =
    `${Constants.apiBaseUrlV3}/portal/file/heartbeat?link_id=${linkId}` +
    `&view_id=${viewId}&second=${seconds}`;
  if (start) url += '&start=1';
  await sendRequest(url, {
    ...Device.getDeviceHeaders(),
    Accept: 'application/json',
    'HTTP-X-TOKEN': States.mainState.mediaToken,
    'HTTP-X-MODE': States.settingsState.devServer ? 'dev' : '',
  });
}

export async function stop(id: number) {
  const res = await sendRequest(
    `${Constants.apiBaseUrlV3}/portal/file/stop?link_id=${id}`,
    {
      ...Device.getDeviceHeaders(),
      Accept: 'application/json',
      'HTTP-X-TOKEN': States.mainState.mediaToken,
      'HTTP-X-MODE': States.settingsState.devServer ? 'dev' : '',
    },
  );
  if (!res.success) throw new ApiError(res.error.code, res.error.message);
}
